import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCurrentCompanyId } from "hooks/auth";
import {
	createRating,
	selectCategories,
	saveScores,
	saveComments,
	submitRating,
} from "newui/features/MySuppliers/api/supplier-performance-api";
import type {
	CreateRatingPayload,
	SubCriterionRating,
	SupplierRating,
} from "newui/features/MySuppliers/types/supplier-performance.types";
import { showToast } from "utils/helpers";
import { trackRatingSubmitted } from "newui/features/MySuppliers/utils/analytics";

interface SubmitRatingInput {
	createPayload: CreateRatingPayload;
	selectedCategoryIds: number[];
	ratings: Record<number, SubCriterionRating[]>;
}

interface UseCreateRatingOptions {
	onSuccess?: (rating: SupplierRating) => void;
}

export const useCreateRating = (options?: UseCreateRatingOptions) => {
	const companyId = useCurrentCompanyId();
	const queryClient = useQueryClient();

	return useMutation<SupplierRating, Error, SubmitRatingInput>({
		mutationFn: async ({ createPayload, selectedCategoryIds, ratings }) => {
			// Step 1: Create the rating (draft)
			const rating = await createRating(createPayload);

			// Step 2: Select categories
			await selectCategories(rating.id, selectedCategoryIds);

			// Step 3: Save scores
			const allScores: Array<{ sub_criteria_id: number; score: number }> = [];
			const allComments: Array<{ sub_criteria_id: number; comment: string | null }> = [];

			for (const categoryId of selectedCategoryIds) {
				const categoryRatings = ratings[categoryId] ?? [];
				for (const r of categoryRatings) {
					if (r.score > 0) {
						allScores.push({
							sub_criteria_id: r.subCriterionId,
							score: r.score,
						});
					}
					if (r.comment) {
						allComments.push({
							sub_criteria_id: r.subCriterionId,
							comment: r.comment,
						});
					}
				}
			}

			if (allScores.length > 0) {
				await saveScores(rating.id, allScores);
			}

			// Step 4: Save comments (if any)
			if (allComments.length > 0) {
				await saveComments(rating.id, allComments);
			}

			// Step 5: Submit
			const submitted = await submitRating(rating.id);
			return submitted;
		},
		onSuccess: (data) => {
			trackRatingSubmitted(data.supplier_company?.id ?? 0, data.overall_score ?? 0);
			if (companyId) {
				queryClient.invalidateQueries({
					queryKey: ["my-suppliers", companyId],
				});
				queryClient.invalidateQueries({
					queryKey: ["supplier-detail", companyId],
				});
				queryClient.invalidateQueries({
					queryKey: ["supplier-ratings", companyId],
				});
			}
			showToast("Performance rating submitted successfully", "Success", true);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			showToast(
				error.message || "Failed to submit performance rating. Please try again.",
				"Error"
			);
		},
	});
};
